import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  despachoService,
  DespachoCliente,
  DespachoOperacion,
} from '../services/despachoService';

/**
 * Vista de impresión por faena o por cliente (2026-06-15).
 * - /despacho/imprimir/cliente/:clienteId  → todas las faenas
 * - /despacho/imprimir/operacion/:clienteId/:operacionId → 1 faena
 *
 * Fase 6 (2026-07-01): filtrado opcional por segmento vía query string
 *   ?segmento=dia   → SOLO ítems del segmento DÍA + subtotales/firmas del día
 *   ?segmento=noche → SOLO ítems del segmento NOCHE + subtotales/firmas de la noche
 * Sin el query param → comprobante clásico (todos los ítems juntos), 100%
 * intacto para operaciones con flag_pedido_segmentado=0.
 *
 * Incluye logo NexGen, fecha, cliente, faena+dirección+
 * observaciones_despacho, ítems por tipo de plato (bebidas separadas
 * con sub-total), totales (comida / bebidas), línea de recepción
 * con espacio para firma.
 *
 * Fix tanda3 H9 (2026-07-01): el auto-print al montar
 * (`setTimeout(window.print, 400)`) abría el diálogo nativo del
 * navegador ANTES de que el usuario pudiera ver el comprobante, y
 * congelaba el group de tabs de Chrome. Ahora la vista se renderiza
 * en pantalla; el botón "🖨️ Imprimir" (visible en no-print) lo
 * dispara solo cuando el usuario lo decide.
 *
 * Sin tocar persistencia — sólo consume GET /api/despacho/registros
 * (sin filtrar al cliente; el componente filtra acá).
 */
type Params = { clienteId?: string; operacionId?: string };

type SegmentoFiltro = 'dia' | 'noche' | 'transversal';

const SEGMENTO_META: Record<
  SegmentoFiltro,
  { titulo: string; sub: string; accent: string; bg: string }
> = {
  dia: {
    titulo: 'SERVICIO DÍA · ALMUERZO',
    sub: 'Entrega del turno de almuerzo',
    accent: '#B45309', // amber-700
    bg: '#FEF3C7', // amber-100
  },
  noche: {
    titulo: 'SERVICIO NOCHE · CENA',
    sub: 'Entrega del turno de cena',
    accent: '#3730A3', // indigo-800
    bg: '#E0E7FF', // indigo-100
  },
  transversal: {
    titulo: 'ÍTEMS TRANSVERSALES',
    sub: 'Ítems compartidos entre servicios',
    accent: '#334155', // slate-700
    bg: '#F1F5F9', // slate-100
  },
};

const BEBIDA_TIPO = /bebida/i;

export default function DespachoImprimir() {
  const params = useParams() as Params;
  const [searchParams] = useSearchParams();
  const segmentoRaw = searchParams.get('segmento');
  const segmentoFiltro: SegmentoFiltro | null =
    segmentoRaw === 'dia' || segmentoRaw === 'noche' || segmentoRaw === 'transversal'
      ? segmentoRaw
      : null;
  const clienteIdNum = params.clienteId ? parseInt(params.clienteId) : NaN;
  const operacionIdNum = params.operacionId
    ? parseInt(params.operacionId)
    : NaN;
  const modoFaena = !isNaN(operacionIdNum);

  const [data, setData] = useState<DespachoCliente[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // modo=todos para que la impresión sirva tanto para vigentes como
    // para reimpresión post-entrega (cuando el comprobante necesita
    // mostrar quién entregó / quién recibió).
    despachoService
      .listar({ modo: 'todos' })
      .then((d) => setData(d))
      .finally(() => setLoading(false));
  }, []);

  // Fix tanda3 H9 (2026-07-01): removido el auto-print. El usuario
  // ve el comprobante en pantalla y decide cuándo imprimir con el
  // botón "🖨️ Imprimir" del header no-print.

  const cliente = useMemo(
    () => data.find((c) => c.cliente_id === clienteIdNum) || null,
    [data, clienteIdNum]
  );

  if (loading) {
    return (
      <div className="p-8 text-center text-gray-500">
        Cargando comprobante…
      </div>
    );
  }

  if (!cliente) {
    return (
      <div className="p-8 text-center text-gray-500">
        Sin pedidos para imprimir.
      </div>
    );
  }

  const operacionesBase = modoFaena
    ? cliente.operaciones.filter((o) => o.operacion_id === operacionIdNum)
    : cliente.operaciones;

  // Fase 6: si hay `?segmento=...`, filtramos cada pedido dejando SOLO los
  // registros que pertenecen al segmento pedido. Un pedido queda fuera si
  // no tiene ningún registro del segmento. Una operación queda fuera si
  // ninguno de sus pedidos tiene registros del segmento.
  const operacionesAImprimir: DespachoOperacion[] = segmentoFiltro
    ? operacionesBase
        .map((o) => ({
          ...o,
          pedidos: o.pedidos
            .map((p) => ({
              ...p,
              registros: p.registros.filter(
                (r) =>
                  (r.segmento ?? 'transversal') === segmentoFiltro
              ),
            }))
            .filter((p) => p.registros.length > 0),
        }))
        .filter((o) => o.pedidos.length > 0)
    : operacionesBase;

  const totalesCliente = operacionesAImprimir.reduce(
    (acc, o) => {
      const t = totalesOperacion(o);
      return {
        comida: acc.comida + t.comida,
        bebidas: acc.bebidas + t.bebidas,
      };
    },
    { comida: 0, bebidas: 0 }
  );

  const metaSegmento = segmentoFiltro ? SEGMENTO_META[segmentoFiltro] : null;
  if (segmentoFiltro && operacionesAImprimir.length === 0) {
    return (
      <div className="p-8 text-center text-gray-500">
        <p className="mb-2">
          Sin ítems del segmento{' '}
          <strong>{SEGMENTO_META[segmentoFiltro].titulo}</strong> para este
          cliente.
        </p>
        <p className="text-xs">
          Puede que el pedido de esa tanda aún no haya sido despachado.
        </p>
      </div>
    );
  }

  const hoy = new Date().toLocaleDateString('es-CL', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <div className="print-root max-w-[800px] mx-auto p-8 text-gray-900 bg-white">
      <style>{`
        @media print {
          @page { margin: 12mm; size: A4; }
          body { background: white !important; }
          .print-root { padding: 0 !important; }
          .no-print { display: none !important; }
        }
        .nexgen-cyan { color: #29ABE2; }
        .nexgen-orange { color: #F7941D; }
        table.items { width: 100%; border-collapse: collapse; }
        table.items th, table.items td {
          padding: 6px 8px; text-align: left; font-size: 12px;
          border-bottom: 1px solid #e5e7eb;
        }
        table.items th { background: #f9fafb; font-weight: 600; }
        h2.faena { font-size: 14px; color: #29ABE2; margin: 16px 0 4px; }
        .obs-cliente { background: #FFF7ED; border-left: 3px solid #F7941D; padding: 6px 10px; font-size: 12px; }
      `}</style>

      <div className="no-print mb-4 flex justify-end">
        <button
          onClick={() => window.print()}
          className="px-4 py-2 bg-[#29ABE2] text-white rounded text-sm font-medium"
        >
          🖨️ Imprimir
        </button>
      </div>

      <header
        className="flex items-center justify-between border-b-2 pb-3 mb-4"
        style={{
          borderColor: metaSegmento ? metaSegmento.accent : '#29ABE2',
        }}
      >
        <div>
          <h1
            className="text-2xl font-bold"
            style={{ color: metaSegmento ? metaSegmento.accent : '#29ABE2' }}
          >
            {metaSegmento ? 'Comprobante de entrega' : 'Despacho'}
          </h1>
          <p className="text-xs text-gray-600">
            {metaSegmento
              ? metaSegmento.sub
              : 'Comprobante de entrega'}
          </p>
        </div>
        <div className="text-right">
          <div className="text-lg font-bold nexgen-orange">NexGen</div>
          <p className="text-xs text-gray-600">Suminuta · {hoy}</p>
        </div>
      </header>

      {metaSegmento && (
        <div
          className="rounded-md mb-4 px-4 py-2 text-sm font-semibold uppercase tracking-wide"
          style={{
            background: metaSegmento.bg,
            color: metaSegmento.accent,
            border: `1px solid ${metaSegmento.accent}`,
          }}
          data-testid={`cabecera-segmento-${segmentoFiltro}`}
        >
          {metaSegmento.titulo}
        </div>
      )}

      <section className="mb-4 space-y-2">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h2 className="text-base font-semibold">
            {cliente.cliente_nombre}
          </h2>
          <div className="text-xs text-gray-600">
            Cliente #{cliente.cliente_id}
          </div>
        </div>
        {cliente.observaciones_despacho && (
          <div className="obs-cliente">
            <strong>Observaciones de despacho:</strong>{' '}
            {cliente.observaciones_despacho}
          </div>
        )}
      </section>

      {operacionesAImprimir.map((op) => {
        const t = totalesOperacion(op);
        return (
          <section key={op.operacion_id} className="mb-6">
            <h2 className="faena">
              {op.operacion_nombre}
              {(op.direccion || op.comuna || op.ciudad) && (
                <span className="font-normal text-gray-700 text-xs ml-2">
                  · {[op.direccion, op.comuna, op.ciudad]
                    .filter(Boolean)
                    .join(', ')}
                </span>
              )}
            </h2>

            <table className="items">
              <thead>
                <tr>
                  <th style={{ width: '50%' }}>Plato / Ítem</th>
                  <th style={{ width: '25%' }}>Tipo</th>
                  <th style={{ width: '15%', textAlign: 'right' }}>Cantidad</th>
                  <th style={{ width: '10%' }}>Pedido</th>
                </tr>
              </thead>
              <tbody>
                {op.pedidos.flatMap((p) =>
                  p.registros.map((r) => (
                    <tr key={r.id}>
                      <td>{r.receta_nombre}</td>
                      <td>{r.tipo_plato || '-'}</td>
                      <td style={{ textAlign: 'right' }}>{r.cantidad}</td>
                      <td>{p.pedido_codigo}</td>
                    </tr>
                  ))
                )}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={2} style={{ fontWeight: 600 }}>
                    Subtotal comida
                  </td>
                  <td style={{ textAlign: 'right', fontWeight: 600 }}>
                    {t.comida}
                  </td>
                  <td />
                </tr>
                <tr>
                  <td colSpan={2} style={{ fontWeight: 600 }}>
                    Subtotal bebidas
                  </td>
                  <td style={{ textAlign: 'right', fontWeight: 600 }}>
                    {t.bebidas}
                  </td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </section>
        );
      })}

      {!modoFaena && (
        <section className="border-t-2 border-[#F7941D] pt-3 mb-6">
          <h2 className="text-sm font-semibold mb-2">Totales del cliente</h2>
          <table className="items">
            <tbody>
              <tr>
                <td>Comida (porciones)</td>
                <td style={{ textAlign: 'right', fontWeight: 700 }}>
                  {totalesCliente.comida}
                </td>
              </tr>
              <tr>
                <td>Bebidas</td>
                <td style={{ textAlign: 'right', fontWeight: 700 }}>
                  {totalesCliente.bebidas}
                </td>
              </tr>
            </tbody>
          </table>
        </section>
      )}

      <section className="mt-12 pt-6 border-t border-gray-300">
        {(() => {
          // Si hay nombres ya registrados (reimpresión post-entrega),
          // los pre-llenamos en las líneas de firma.
          const regs = operacionesAImprimir.flatMap((o) =>
            o.pedidos.flatMap((p) => p.registros)
          );
          const entreganNombres = Array.from(
            new Set(
              regs
                .map((r) => r.entregado_por)
                .filter((s): s is string => !!s && s.trim().length > 0)
            )
          ).join(' / ');
          const recibeNombres = Array.from(
            new Set(
              regs
                .map((r) => r.recibido_por)
                .filter((s): s is string => !!s && s.trim().length > 0)
            )
          ).join(' / ');
          const fechasEntrega = Array.from(
            new Set(
              regs
                .map((r) =>
                  r.fecha_entrega_cliente
                    ? new Date(r.fecha_entrega_cliente).toLocaleString('es-CL', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })
                    : ''
                )
                .filter(Boolean)
            )
          ).join(' · ');
          return (
            <>
              <div className="grid grid-cols-2 gap-8">
                <div>
                  <div className="border-t border-gray-700 mt-12 pt-2 text-center text-xs">
                    {recibeNombres || 'Recibe (nombre y firma)'}
                  </div>
                </div>
                <div>
                  <div className="border-t border-gray-700 mt-12 pt-2 text-center text-xs">
                    {entreganNombres || 'Entrega (despacho)'}
                  </div>
                </div>
              </div>
              {fechasEntrega && (
                <p className="text-[10px] text-center text-gray-500 mt-2">
                  Entregado el {fechasEntrega}
                </p>
              )}
              <p className="text-[10px] text-center text-gray-500 mt-4">
                NexGen · Suminuta · {hoy}
              </p>
            </>
          );
        })()}
      </section>
    </div>
  );
}

function totalesOperacion(op: DespachoOperacion): {
  comida: number;
  bebidas: number;
} {
  let comida = 0;
  let bebidas = 0;
  for (const p of op.pedidos) {
    for (const r of p.registros) {
      if (r.tipo_plato && BEBIDA_TIPO.test(r.tipo_plato)) {
        bebidas += r.cantidad;
      } else {
        comida += r.cantidad;
      }
    }
  }
  return { comida, bebidas };
}
